'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from '@trashlab/sitesapp/src/components/shared/link';
import { useTenantContext } from '@trashlab/sitesapp/src/contexts/use-tenant-context';
import { trpcWebsiteSubmitServiceRequest } from '@trashlab/api/src/generated/hooks';
import { PageSEO } from '@trashlab/sitesapp/src/templates/redebins/components/shared/page-seo';

// Form validation schema
const junkRemovalFormSchema = z.object({
  preferredServiceDate: z.string().min(1, 'Service date is required'),
  streetAddress: z.string().min(1, 'Street address is required'),
  city: z.string().min(1, 'City is required'),
  zipCode: z.string().min(1, 'ZIP code is required'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  phone: z.string().min(10, 'Valid phone number is required'),
  email: z.string().email('Valid email is required'),
  projectDescription: z.string().optional(),
});

type JunkRemovalFormData = z.infer<typeof junkRemovalFormSchema>;

export const JunkRemovalContent = () => {
  const { website } = useTenantContext();
  const [isSuccess, setIsSuccess] = useState(false);

  const submitServiceRequestMutation =
    trpcWebsiteSubmitServiceRequest.useMutation();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<JunkRemovalFormData>({
    resolver: zodResolver(junkRemovalFormSchema),
  });

  const onSubmit = async (data: JunkRemovalFormData) => {
    if (!website?.organization?.id || !website?.id || !website?.domain?.id) {
      setError('root', {
        message: 'Unable to submit request. Please try again later.',
      });
      return;
    }

    try {
      // Combine address and project description into serviceRequested
      const serviceRequested = `JUNK REMOVAL REQUEST

Address: ${data.streetAddress}, ${data.city}, ${data.zipCode}

Project Description: ${data.projectDescription || 'No description provided'}`;

      // Convert date to ISO-8601 DateTime format
      const preferredServiceDateISO = data.preferredServiceDate
        ? new Date(data.preferredServiceDate).toISOString()
        : undefined;

      await submitServiceRequestMutation.mutateAsync({
        tenantId: website.organization.id,
        websiteId: website.id,
        domainId: website.domain.id,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        serviceRequested,
        preferredServiceDate: preferredServiceDateISO,
      });

      reset();
      setIsSuccess(true);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred';
      setError('root', { message: errorMessage });
    }
  };

  return (
    <>
      <PageSEO pathname="/junk-removal" />
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Intro Section */}
            <div className="mb-12 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-[#333333] mb-4 italic">
                RED-E When You Are!
            </h2>
            <h3 className="text-2xl font-bold text-[#333333] mb-6 italic">
              Get Rid Of Your Junk Today!
            </h3>
            <div className="space-y-4 text-[#333333] max-w-3xl mx-auto">
              <p>
                Need help clearing out the clutter? RED-E Removal is here to help, whether it's old furniture, appliances, or other general debris. Simply fill out the form below and describe your project. Alternatively, you can{' '}
                <a href="tel:9529312577" className="text-[#E31837] hover:underline">
                  text
                </a>{' '}
                us photos of the items you'd like picked up. We'll respond back with a quote. If you have a larger project, we can visit you on site and give you a free estimate. Already have a quote from someone else? Perfect, let us know, and we will beat it.
              </p>
              <p>
                Need a dumpster and have large items you can't lift yourself?{' '}
                <a href="tel:9529312577" className="text-[#E31837] hover:underline">
                  Ask
                </a>{' '}
                about our combo junk removal & dumpster package today.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Left Column - Services */}
            <div className="space-y-6">
              <div className="border-2 border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-bold text-[#333333] mb-4 italic">
                  Introducing Our Full-Service Junk Removal:
                </h3>
                <ul className="space-y-2 text-[#333333]">
                  <li>Will match or beat any competing estimate</li>
                  <li>Cheaper than most national brands</li>
                  <li>We cater to both home and business</li>
                  <li>Pricing is suited to each job</li>
                  <li>We take just about everything</li>
                  <li>
                    <a href="tel:9529312577" className="text-[#E31837] hover:underline">
                      Text
                    </a>{' '}
                    us photos of your debris and receive a quote
                  </li>
                </ul>
              </div>

              <div className="border-2 border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-bold text-[#333333] mb-4 italic">
                  What We Take (Just About Everything):
                </h3>
                <ul className="space-y-2 text-[#333333]">
                  <li>E-Waste, Televisions, and Appliances</li>
                  <li>Furniture and Mattresses</li>
                  <li>Demolition Debris and Concrete</li>
                  <li>Hot Tubs, Pools Tables, and Pianos</li>
                  <li>BAGSTERS and the Alike</li>
                  <li>Full Service Junk Removal</li>
                  <li>And Just About Everything Else</li>
                </ul>
              </div>

              <div className="border-2 border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-bold text-[#333333] mb-4 italic">
                  What Sets Us Apart? We've got both!
                </h3>
                <p className="text-[#333333]">
                  Schedule your junk removal and dumpster rental with the same company. Let us do the lifting, we work hard to make your experience quick, convenient and hassle free.
                </p>
              </div>
              <ul className="space-y-3 text-[#333333]">
                <li>
                  <strong>"Dumpster Benefits:"</strong> Extended on-site duration for a relaxed cleanup experience.{' '}
                  <Link href="/dumpster-sizes" className="text-[#E31837] hover:underline">
                    Check out our bin sizes!
                  </Link>
                </li>
                <li>
                  <strong>"Junk Removal Excellence:"</strong> Perfect for larger, heavier items requiring extra muscle.
                </li>
              </ul>
            </div>

            {/* Right Column - Form */}
            <div>
              <h2 className="text-2xl font-bold text-[#333333] mb-4 italic">
                Junk Removal Request
              </h2>
              <h3 className="text-xl font-bold text-[#333333] mb-4 italic">
                How It Works:
              </h3>
              <ul className="space-y-3 text-[#333333] mb-8">
                <li>
                  <strong>
                    <a href="tel:9529312577" className="text-[#E31837] hover:underline">
                      Text
                    </a>{' '}
                    us or Fill Out the Form Below:
                  </strong>{' '}
                  Provide project details.
                </li>
                <li>
                  <strong>"Receive Estimate:"</strong> A representative will promptly respond.
                </li>
                <li>
                  <strong>"Sit Back and Relax:"</strong> Point, and watch your junk disappear.
                </li>
                <li>
                  <strong>"Same Day Service Available:"</strong> Just like our dumpster rentals, our junk removal service offers same-day excellence!
                </li>
                <li>
                  <strong>
                    Contact us today at{' '}
                    <a href="tel:9529312577" className="text-[#E31837] hover:underline">
                      952-931-2577
                    </a>
                    :
                  </strong>{' '}
                  Transform your space effortlessly with Red-E-Bins – your trusted partner in hassle-free cleanup!
                </li>
              </ul>

              {/* Form */}
              <div className="bg-gray-50 rounded-lg p-6">
                {isSuccess ? (
                  <div className="text-center py-8">
                    <div className="text-green-600 text-5xl mb-4">✓</div>
                    <h3 className="text-xl font-bold text-[#333333] mb-2">
                      Request Submitted!
                    </h3>
                    <p className="text-[#666666] mb-6">
                      We have received your junk removal request and will get
                      back to you as soon as possible.
                    </p>
                    <button
                      onClick={() => setIsSuccess(false)}
                      className="bg-[#E31837] text-white px-6 py-2 rounded-md font-semibold hover:bg-[#C1152E] transition-colors"
                    >
                      Submit Another Request
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    {errors.root && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                        {errors.root.message}
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-semibold text-[#333333] mb-2">
                        Requested Service Date{' '}
                        <span className="text-[#E31837]">*</span>
                      </label>
                      <input
                        type="date"
                        {...register('preferredServiceDate')}
                        className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#E31837] ${
                          errors.preferredServiceDate
                            ? 'border-red-500'
                            : 'border-gray-300'
                        }`}
                      />
                      {errors.preferredServiceDate && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.preferredServiceDate.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#333333] mb-2">
                        Service Address{' '}
                        <span className="text-[#E31837]">*</span>
                      </label>
                      <div className="space-y-2">
                        <input
                          type="text"
                          placeholder="Street Address"
                          {...register('streetAddress')}
                          className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#E31837] ${
                            errors.streetAddress
                              ? 'border-red-500'
                              : 'border-gray-300'
                          }`}
                        />
                        {errors.streetAddress && (
                          <p className="text-red-500 text-sm">
                            {errors.streetAddress.message}
                          </p>
                        )}
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <input
                              type="text"
                              placeholder="City"
                              {...register('city')}
                              className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#E31837] ${
                                errors.city
                                  ? 'border-red-500'
                                  : 'border-gray-300'
                              }`}
                            />
                            {errors.city && (
                              <p className="text-red-500 text-sm mt-1">
                                {errors.city.message}
                              </p>
                            )}
                          </div>
                          <div>
                            <input
                              type="text"
                              placeholder="ZIP / Postal Code"
                              {...register('zipCode')}
                              className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#E31837] ${
                                errors.zipCode
                                  ? 'border-red-500'
                                  : 'border-gray-300'
                              }`}
                            />
                            {errors.zipCode && (
                              <p className="text-red-500 text-sm mt-1">
                                {errors.zipCode.message}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#333333] mb-2">
                        Name <span className="text-[#E31837]">*</span>
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <input
                            type="text"
                            placeholder="First"
                            {...register('firstName')}
                            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#E31837] ${
                              errors.firstName
                                ? 'border-red-500'
                                : 'border-gray-300'
                            }`}
                          />
                          {errors.firstName && (
                            <p className="text-red-500 text-sm mt-1">
                              {errors.firstName.message}
                            </p>
                          )}
                        </div>
                        <div>
                          <input
                            type="text"
                            placeholder="Last"
                            {...register('lastName')}
                            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#E31837] ${
                              errors.lastName
                                ? 'border-red-500'
                                : 'border-gray-300'
                            }`}
                          />
                          {errors.lastName && (
                            <p className="text-red-500 text-sm mt-1">
                              {errors.lastName.message}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#333333] mb-2">
                        Phone <span className="text-[#E31837]">*</span>
                      </label>
                      <input
                        type="tel"
                        placeholder="Phone*"
                        {...register('phone')}
                        className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#E31837] ${
                          errors.phone ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.phone && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.phone.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#333333] mb-2">
                        Email <span className="text-[#E31837]">*</span>
                      </label>
                      <input
                        type="email"
                        placeholder="Email*"
                        {...register('email')}
                        className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#E31837] ${
                          errors.email ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.email && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.email.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#333333] mb-2">
                        Please briefly describe your project:
                      </label>
                      <textarea
                        placeholder="Please briefly describe your project:"
                        rows={5}
                        {...register('projectDescription')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#E31837]"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#E31837] text-white px-6 py-3 rounded-md font-semibold hover:bg-[#C1152E] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
          </div>
        </div>
      </section>
    </>
  );
};

